import { AssistantChatTransport } from '@assistant-ui/react-ai-sdk';
import { HttpChatTransportInitOptions, UIMessage } from 'ai';

// mastra workflow resume streams omit the `tool-input-available` chunk because
// it was already emitted on the prior assistant message
// ai sdk stream validator throws when it sees a `tool-output-*` without a matching
// `tool-input-available` in the same response's state, which aborts the fetch
// this custom transport writes synthetic tool-input-availaable as a workaround
// https://github.com/mastra-ai/mastra/issues/8776
type ChatResumeAwareTransportOptions<UI_MESSAGE extends UIMessage> =
  HttpChatTransportInitOptions<UI_MESSAGE> & {
    getResumeToolName?: () => string | undefined;
  };

class ChatResumeAwareTransport<
  UI_MESSAGE extends UIMessage,
> extends AssistantChatTransport<UI_MESSAGE> {
  private readonly getResumeToolName: () => string | undefined;

  private resumeToolName: string | undefined;

  constructor(options: ChatResumeAwareTransportOptions<UI_MESSAGE> = {}) {
    const { getResumeToolName, ...rest } = options;
    super(rest);
    this.getResumeToolName = getResumeToolName ?? (() => undefined);
  }

  async sendMessages(
    ...args: Parameters<AssistantChatTransport<UI_MESSAGE>['sendMessages']>
  ) {
    this.resumeToolName = this.getResumeToolName();
    return super.sendMessages(...args);
  }

  processResponseStream(stream: ReadableStream<Uint8Array>) {
    const base = super.processResponseStream(stream);
    const seenInputAvailable = new Set<string>();
    const resumeToolName = this.resumeToolName;

    return base.pipeThrough(
      new TransformStream({
        transform(chunk, controller) {
          const { type, toolCallId } = chunk as {
            type: string;
            toolCallId?: string;
          };

          const isInputAvailable = type === 'tool-input-available';

          const isToolOutput =
            type === 'tool-output-available' || type === 'tool-output-error';

          const isOrphanOutput =
            isToolOutput && !!toolCallId && !seenInputAvailable.has(toolCallId);

          if (isOrphanOutput && !resumeToolName) {
            return;
          }

          if (isInputAvailable && toolCallId) {
            seenInputAvailable.add(toolCallId);
          } else if (isOrphanOutput && toolCallId) {
            controller.enqueue({
              type: 'tool-input-available',
              toolCallId,
              toolName: resumeToolName,
              input: {},
            } as any);

            seenInputAvailable.add(toolCallId);
          }

          controller.enqueue(chunk);
        },
      }),
    );
  }
}

export default ChatResumeAwareTransport;
